import React, { useEffect, useState, useRef } from 'react';
import { PitchDeck } from './pages/PitchDeck';
import { AdvantageSummary } from './pages/AdvantageSummary';
import { PricingProvider } from './contexts/PricingContext';
import { SystemPage } from './pages/SystemPage';
import { LeadershipPage } from './pages/LeadershipPage';
import { ComparePage } from './pages/ComparePage';
import { OnePager } from './pages/OnePager';
import { D2DLaunchOnePager } from './pages/D2DLaunchOnePager';
import { LayoutGrid, Calculator, Lock, Newspaper, Rocket, Menu } from 'lucide-react';
type ViewType = 'deck' | 'advantage' | 'system' | 'leadership' | 'compare' | 'onepager' | 'd2dlaunch';
export function App() {
  // ALL hooks at the top
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [view, setView] = useState<ViewType>('deck');
  const [menuOpen, setMenuOpen] = useState(false);
  // Scroll position management
  const scrollPositions = useRef<Record<string, number>>({});
  // Check auth on mount
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem('site_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Restore scroll position when view changes
  useEffect(() => {
    // Small timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[view] || 0);
    }, 0);
    return () => clearTimeout(timer);
  }, [view]);
  // Handler functions
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'HIRE100k' || password === 'TheManny') {
      localStorage.setItem('site_authenticated', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };
  const handleViewChange = (newView: ViewType) => {
    // Save current scroll position before switching
    scrollPositions.current[view] = window.scrollY;
    setView(newView);
  };
  // Show loading during initial mount
  if (!isClient) {
    return <div className="min-h-screen bg-[#0A0F1C]" data-id="element-0" data-mp="d57fv4288kjl"></div>;
  }
  // Show login if not authenticated
  if (!isAuthenticated) {
    return <div className="min-h-screen w-full bg-[#0A0F1C] flex items-center justify-center p-4 sm:p-6" data-id="element-1" data-mp="864atjm8yebm">
        <div className="w-full max-w-md" data-id="element-2" data-mp="3glwb3twd07ty">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl" data-id="element-3" data-mp="3bmbazg5gdtfn">
            <div className="flex justify-center mb-6" data-id="element-4" data-mp="3qbfwp7bgifsi">
              <div className="w-16 h-16 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 flex items-center justify-center" data-id="element-5" data-mp="3qdajkr6aghz8">
                <Lock className="w-8 h-8 text-[#7FE0C3]" data-id="element-6" data-mp="2xudb7vxmt9w5" />
              </div>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-2" data-id="element-7" data-mp="2sw1yndfycla8">
              Welcome to HIRE
            </h1>
            <p className="text-sm sm:text-base text-slate-400 text-center mb-8" data-id="element-8" data-mp="18czn0iw5pdmh">
              Please enter your access code to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-9" data-mp="yflkzoazcrdp">
              <div data-id="element-10" data-mp="yhepawy4ezij">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-11" data-mp="ydqwk00i2h4v">
                  Access Code
                </label>
                <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#7FE0C3] focus:border-transparent transition-all text-base" placeholder="Enter access code" autoFocus data-id="element-12" data-mp="1w3c16bhzw6li" />
              </div>

              {error && <div className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 rounded-lg py-2" data-id="element-13" data-mp="qyqdzww76nck">
                  {error}
                </div>}

              <button type="submit" className="w-full px-4 py-3 bg-[#7FE0C3] hover:bg-[#5ACCA8] text-[#163A5F] font-medium rounded-lg transition-all transform active:scale-95" data-id="element-14" data-mp="h842oevikizo">
                Access Site
              </button>
            </form>
          </div>
        </div>
      </div>;
  }
  // Main content
  return <PricingProvider data-id="element-15" data-mp="zxam6444temj">
    <div className="relative" data-id="element-16" data-mp="zwsg6fro3m1f">
      {view === 'deck' && <PitchDeck data-id="element-17" data-mp="vw8nboe4n4l3" />}
      {view === 'advantage' && <AdvantageSummary data-id="element-18" data-mp="vv71covkoxol" />}
      {view === 'system' && <SystemPage data-id="element-19" data-mp="vykzfkx7ekq8" />}
      {view === 'leadership' && <LeadershipPage data-id="element-20" data-mp="3kp41sq7ftjba" />}
      {view === 'compare' && <ComparePage data-id="element-21" data-mp="3klq1rom8xm70" />}
      {view === 'onepager' && <OnePager data-id="element-22" data-mp="3glehwe1pjpwl" />}
      {view === 'd2dlaunch' && <D2DLaunchOnePager data-id="element-23" data-mp="3gok85wm2oxtw" />}

      {/* Left Side Menu */}
      <div className="no-print fixed top-3 left-3 sm:top-6 sm:left-6 z-[100] group" onMouseLeave={() => setMenuOpen(false)} data-id="element-24" data-mp="1w5gd6omrhs37">
        <button type="button" onMouseEnter={() => setMenuOpen(true)} onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="Open navigation menu" className="px-3 py-2 sm:px-4 sm:py-2 bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-full shadow-xl text-slate-400 hover:text-white hover:border-[#7FE0C3]/50 transition-colors duration-150 ease-out flex items-center justify-center gap-2 text-xs sm:text-sm font-medium min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-25" data-mp="203nubkl50vex">
          <Menu className="w-4 h-4" data-id="element-26" data-mp="thlbty3w8tyu" />
          <span className="hidden sm:inline" data-id="element-27" data-mp="timzr360daxz">Menu</span>
        </button>

        <div className={`absolute top-full left-0 pt-2 w-52 origin-top-left transition-[opacity,transform] duration-150 ease-out ${menuOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`} data-id="element-28" data-mp="ygeoxpf9qiyh">
          <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl shadow-2xl p-1.5 space-y-0.5" data-id="element-29" data-mp="1wu28216b5v2p">
            <a href="https://roi.d2dhire.com" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-30" data-mp="2apw5no1msy0i">
              <Calculator className="w-4 h-4 shrink-0" data-id="element-31" data-mp="2tj021h68hoyt" />
              ROI Calculator
            </a>
            {[{
              id: 'deck' as ViewType,
              label: 'Deck',
              icon: LayoutGrid
            }, {
              id: 'onepager' as ViewType,
              label: 'One-Pager',
              icon: Newspaper
            }, {
              id: 'd2dlaunch' as ViewType,
              label: 'D2D Launch',
              icon: Rocket
            }].map(item => <button key={item.id} onClick={() => {
              handleViewChange(item.id);
              setMenuOpen(false);
            }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ease-out ${view === item.id ? 'bg-[#7FE0C3] text-[#163A5F]' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-32" data-mp="3n1enxi9l2hag">
                <item.icon className="w-4 h-4 shrink-0" data-id="element-33" data-mp="2pez8sn4mpvq2" />
                {item.label}
              </button>)}
          </div>
        </div>
      </div>

    </div>
    </PricingProvider>;
}